import { getApiBaseUrl } from "../util";
const defaultInterceptors = {
    response(response) {
        if (response.headers.get("content-type").indexOf("application/json") > -1) {
            return response.json();
        } else {
            return response;
        }
    },
    responseError(error) {
        return Promise.reject(error);
    }
};

const defaultRequestHeaders = {
    "Content-Type": "application/json"
};

const isArray = obj => {
    return Object.prototype.toString.call(obj).slice(8, -1) === "Array";
};

export const _decorateRequest = (requestHeaders, token, requestOptions) => {
    requestOptions.headers = {
        ...defaultRequestHeaders,
        ...requestHeaders,
        ...requestOptions.headers
    };
    if (token) {
        requestOptions.headers.Authorization = token;
    }
    return requestOptions;
};

export const _validTokenForRequest = (verifyToken, token) => {
    if (!verifyToken) {
        return true;
    }
    try {
        const payload = token.split(".")[1];
        const expirationTime = parseInt(JSON.parse(atob(payload)).exp, 10) * 1000;
        if (expirationTime - 6000 > Date.now()) {
            return true;
        }
        return false;
    } catch (ex) {
        return false;
    }
};

export const _responseSignalsTokenRefresh = (verifyResponse, response) => {
    if (!verifyResponse) {
        return false;
    }
    return response.status === 401;
};

const getTokenSource = options => {
    let responseInterceptors;
    const { requestHeaders, responseHandlers, refreshToken, verifyToken = true, verifyResponse = true } = options;
    if (isArray(responseHandlers) && responseHandlers.length > 0) {
        responseInterceptors = responseHandlers;
    } else {
        responseInterceptors = [defaultInterceptors];
    }
    return {
        name: "default-token-source",
        decorateRequest({ token, requestOptions }) {
            return _decorateRequest(requestHeaders, token, requestOptions);
        },
        refreshToken(callback) {
            const wrappedCallback = (err, data) => {
                if (data) {
                    window.localStorage.setItem("UIMToken", data);
                } else {
                    window.localStorage.removeItem("UIMToken");
                }
                callback(err, data);
            };
            refreshToken && refreshToken(wrappedCallback);
        },
        validTokenForRequest(token) {
            return _validTokenForRequest(verifyToken, token);
        },
        responseSignalsTokenRefresh(response) {
            return _responseSignalsTokenRefresh(verifyResponse, response);
        },
        responseInterceptors: responseInterceptors
    };
};

function addRequest(activeRequests, request) {
    activeRequests.push(request);
    return () => {
        const index = activeRequests.indexOf(request);
        activeRequests.splice(index, 1);
    };
}

export const DataProviderHelperMixin = {
    name: "data-provider-helper-mixin",
    inject: {
        dataProvider: {
            default: () => null
        }
    },
    data() {
        return {
            activeRequests: []
        };
    },
    computed: {
        localDataProvider() {
            return this.dataProvider || this.globalDataProvider || window.mwc.dataAccess;
        }
    },
    methods: {
        $fetch(options) {
            const request = this.localDataProvider.fetch(options);
            const removeRequest = addRequest(this.activeRequests, request);
            const { id, responseInterceptors = [defaultInterceptors] } = request;
            let promise = request.then(
                response => {
                    removeRequest();
                    return response;
                },
                error => {
                    removeRequest();
                    return Promise.reject(error);
                }
            );
            responseInterceptors.forEach(({ response, responseError }) => {
                if (response || responseError) {
                    promise = promise.then(response, responseError);
                }
            });
            Object.defineProperty(promise, "id", {
                value: id
            });
            return promise;
        },
        $abort(request) {
            return this.localDataProvider.abort(request);
        },
        $abortActiveRequests() {
            this.activeRequests.forEach(request => {
                this.localDataProvider.abort(request);
            });
        }
    }
};

export default {
    provide() {
        return {
            dataProvider: this.globalDataProvider
        };
    },
    data() {
        return {
            defaultConfig: {
                settings: {
                    useInternalRegisteredTokenSource: true,
                    tokenSourceOptions: null,
                    tokenSourceName: "uim"
                }
            },
            globalDataProvider: {
                fetch: this.fetchInternal,
                abort: window.mwc.dataAccess.abort
            }
        };
    },
    created() {
        this.useInternalTokenSource = this.settings.useInternalRegisteredTokenSource && this.settings.tokenSourceOptions;
        if (this.useInternalTokenSource) {
            this.tokenSource = getTokenSource(this.settings.tokenSourceOptions);
            window?.mwc?.dataAccess?.registerTokenSource(this.tokenSource);
        }
    },
    methods: {
        fetchInternal(options) {
            const environment = this.settings && this.settings.environment;
            let sourceName = this.settings.tokenSourceName;
            let responseInterceptors = [];
            let additionalHeaders = {};
            if (this.useInternalTokenSource) {
                ({ requestHeaders: additionalHeaders = {} } = this.settings.tokenSourceOptions);
                ({ name: sourceName, responseInterceptors } = this.tokenSource);
            }
            const useProxy = process.env.VUE_APP_USAGE === "application";
            let { url, authByToken = false, ...requestOptions } = options;
            url = getApiBaseUrl(url, environment, useProxy);
            if (authByToken) {
                requestOptions = {
                    ...requestOptions,
                    source: sourceName
                };
            }
            const request = window.mwc.dataAccess.fetch({
                url,
                credentials: "include",
                headers: {
                    ...defaultRequestHeaders,
                    ...additionalHeaders,
                    ...requestOptions.headers
                },
                ...requestOptions
            });
            request.responseInterceptors = responseInterceptors;
            return request;
        }
    }
};
